<template>
  <Stack gap="0">
    <!-- Entity part -->
    <div
      class="single-node"
      :class="{
        'has-descendants': hasDescendants,
        'is-current-entity': isCurrentEntity,
      }"
    >
      <div
        v-if="level > 0"
        :style="{ '--level': level }"
        class="single-spacer"
      >
        <div
          v-if="hasDescendants"
          class="single-toggle"
          @click="showDescendants = !showDescendants"
        >
          <Icon
            class="single-icon"
            :class="{ 'is-collapsed': !showDescendants }"
            icon="fas fa-caret-down"
          />
        </div>

        <div
          v-else
          class="single-line"
        />
      </div>

      <div
        class="single-body"
        :style="{ '--level': level }"
      >
        <div class="single-info">
          <router-link
            v-if="canNavigate && !isCurrentEntity"
            class="single-name"
            :to="{ name: 'entity-groups', params: { entityExternalId: node.entity?.external_id } }"
          >
            {{ name }}
          </router-link>

          <strong
            v-else
            class="single-name"
          >
            {{ name }}
          </strong>

          <Badge
            v-if="isCurrentEntity"
            status="primary"
            class="single-here"
            bold
            resizable
          >
            You are here
          </Badge>
        </div>

        <EntityTypeBadge :entity-type="inferedType" />

        <Buttons
          class="single-actions"
          gap="1"
          size="3"
        >
          <Button
            v-if="inferedType !== 'account'"
            spacing="tight"
            icon-before="fas fa-plus"
            icon-shade="success"
            @click="openModal('add-child')"
          >
            Add {{ membersTerm }}
          </Button>

          <template v-if="hasParent">
            <Button
              :title="`Move to another ${groupTerm}`"
              spacing="tight"
              icon-after="fas fa-arrows-alt"
              icon-shade="info"
              @click="openModal('move')"
            >
              Move
            </Button>
            <Button
              :title="`Remove from ${groupTerm}`"
              spacing="tight"
              icon-only="fas fa-trash-alt"
              icon-shade="danger"
              @click="openModal('remove')"
            />
          </template>

          <Button
            v-else-if="inferedType !== 'household'"
            spacing="tight"
            icon-after="fas fa-plus"
            @click="openModal('add')"
          >
            Add to {{ groupTerm }}
          </Button>
        </Buttons>
      </div>
    </div>

    <!-- Descendants part -->
    <template
      v-if="hasDescendants && showDescendants"
    >
      <GroupNode
        v-for="descendant in node.descendants"
        :key="descendant.entity.external_id"
        variant="descendant"
        :node="descendant"
        :parent="node.entity"
        :current-entity-id="currentEntityId"
        :level="level + 1"
        @onSuccess="emit('onSuccess')"
      />

      <Buttons
        class="add-descendants"
        :class="`level-${level}`"
        :style="{ '--level': level }"
      >
        <Button
          icon-shade="success"
          class="add-label"
          icon-before="fas fa-plus"
          size="3"
          @click="openModal('add-child')"
        >
          <div>
            <span>Add {{ membersTerm }} to</span> <strong>{{ name }}</strong>
          </div>
        </Button>
      </Buttons>
    </template>

    <StakeModal v-model="showModal">
      <GroupMembershipModal
        :mode="modalMode"
        :node="node"
        :parent="parent"
        :infered-type="inferedType"
        :group-term="groupTerm"
        :members-term="membersTerm"
        @onClose="showModal = false"
        @onSuccess="handleSuccess"
      />
    </StakeModal>
  </Stack>
</template>

<script setup>
import { computed, ref } from 'vue';
import { Badge, Button, Buttons, Icon, Stack, StakeModal } from '@stakeui';
import GroupMembershipModal from './GroupMembershipModal.vue';
import EntityTypeBadge from './EntityTypeBadge.vue';
import { testForFinancialAccount } from '@frontend/shared/Util.js';

const props = defineProps({
  variant: {
    type: String,
    required: false,
    default: '',
  },
  node: {
    type: Object,
    required: true,
  },
  parent: {
    type: Object,
    required: false,
    default: null,
  },
  currentEntityId: {
    type: [String, Number],
    default: null,
  },
  level: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['onSuccess']);

const showModal = ref(false);
const modalMode = ref('add');
const showDescendants = ref(true);

const openModal = mode => {
  modalMode.value = mode;
  showModal.value = true;
};

const handleSuccess = () => {
  showModal.value = false;
  emit('onSuccess');
};

function hasParents(node) {
  return node.parent_entities?.length > 0;
}

const name = computed(() => props.node.entity?.name || 'No name');

// Has 2 levels of parents or is a financial account
const isFinancialAccount = computed(() => props.parent && hasParents(props.parent) ||
  testForFinancialAccount(name.value));
const hasParent = computed(() => props.parent);
const hasDescendants = computed(() => props.node.descendants && props.node.descendants.length > 0);
const allDescendantsAreNonAccounts = computed(() =>
  props.node.descendants?.every(d => !testForFinancialAccount(d.entity?.name || '')),
);
const isHousehold = computed(() => {
  // Either has 2 levels of descendants or all descendants are not accounts
  return (hasDescendants.value && allDescendantsAreNonAccounts.value) ||
  props.node.descendants?.some(d => d.descendants && d.descendants.length > 0);
});

const isCurrentEntity = computed(() => props.node.entity.external_id === props.currentEntityId);
const canNavigate = computed(() => props.node.entity?.external_id &&
  (props.node.entity.user_permissions.includes('view_entity') ||
    props.node.entity.user_permissions.includes('change_entity')));

const inferedType = computed(() => {
  if (isFinancialAccount.value) {
    return 'account';
  }

  if (isHousehold.value) {
    return 'household';
  }

  return props.node.entity?.entity_type || 'person';
});

const groupTerm = computed(() => {
  if (inferedType.value === 'account') {
    return 'Person';
  }

  return 'Household';
});

const membersTerm = computed(() => {
  if (['Prospect', 'Client', 'person'].includes(inferedType.value)) {
    return 'Accounts';
  }

  return 'People';
});
</script>

<style scoped>
.single-node {
  --spacing: 2rem;
  display: flex;
  align-items: center;
  justify-content: start;

  &.has-descendants {
    .single-toggle {
      opacity: 1;
    }
  }

  &.is-current-entity {
    .single-body {
      background-color: var(--stk-primary-90);
    }

    .single-name {
      color: var(--stk-primary-40);
    }
  }

  &:hover {
    .single-toggle {
      color: var(--stk-primary);
    }

    .single-line::before {
      background-color: var(--stk-primary);
    }
  }
}

.single-body {
  border-radius: 0.5em;
  display: flex;
  align-items: center;
  justify-content: start;
  padding: 0.25em;
  gap: 0.5em;
  padding-left: 0.5em;
  flex-grow: 1;
  position: relative;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, box-shadow, color;

  &:hover {
    background-color: #fff;
    box-shadow: inset 0 0 0 1px var(--stk-primary);
    z-index: 2;

    .single-actions {
      opacity: 1;
    }
  }
}

.single-spacer {
  display: flex;
  align-items: center;
  padding-inline-start: calc(calc(var(--level) - 1) * var(--spacing));
  height: var(--spacing);
}

.single-line,
.single-toggle {
  color: var(--stk-border);
  display: flex;
  height: var(--spacing);
  width: calc(var(--spacing) * 0.75);
  align-items: center;
  justify-content: center;
}

.single-toggle {
  border-radius: 0.5em;
  cursor: pointer;
  height: calc(var(--spacing) * 0.75);
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, color;

  &:hover {
    background-color: var(--stk-primary-90);
    color: var(--stk-primary-40) !important;
  }

  .single-icon {
    color: inherit;
    transition-duration: var(--stk-transition-duration);
    transition-property: transform;

    &.is-collapsed {
      transform: rotate(-90deg);
    }
  }
}

.single-line::before {
  content: "";
  height: 100%;
  width: 2px;
  background-color: currentColor;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;
}

.single-info {
  display: flex;
  align-items: center;
  gap: 0.5em;
}

.single-name {
  font-weight: 500;
  color: var(--stk-text-strong);
}

a.single-name {
  cursor: pointer;

  &:hover {
    color: var(--stk-primary-40);
    text-decoration: underline;
  }
}

.single-actions {
  margin-inline-start: auto;
  /* opacity: 0; */
}

.add-descendants {
  margin-inline-start: calc(0.625em + calc(2em * var(--level)));
  padding: 0.5em 0;

  &.level-0:last-child {
    padding-bottom: 0;
  }
}

.add-label {
  color: var(--stk-color-weak);

  strong {
    font-weight: 600;
    color: var(--stk-color-strong);
  }
}
</style>
